import { REQ_METHODS } from './methods';
import { formatPercent, formatNumber } from '../utils/utils';
import {
  mapCompanyEntry,
  mapCompanySearchNameEntry,
  mapIndividualSearchEntry,
  mapIndividualDetailsEntry,
  mapBulkSearchResults,
  mapBulkManualCandidate,
  mapShareholderGraphFigures
} from './dataMappers';
import { PROFILER_FINANCIAL_STATEMENTS, METRIC_GROUP } from '../utils/constants/profilerFinancials';
import { normalizeContactEnrichmentStatus } from '../utils/enrichmentStatus';
import { PUNCTUATION } from '../utils/constants/notation';
import { isInDevelopment } from '../ga4/constants';
import { shouldOpenIndividualFinancials } from '../utils/profiler/shouldOpenIndividualFinancials';

// ============================================
// Shareholder structure mapping (shared by GET_PROFILER_DETAILS and GET_PROFILER_SHAREHOLDER_GRAPH)
// ============================================

const mapShareholderNodes = (nodes) => {
  const currentYear = new Date().getFullYear();
  let isIndependent = false;

  nodes.forEach((node) => {
    if (node.target) {
      node.type = 'target';

      if (node.is_independent) {
        isIndependent = true;
      }
    }

    if (node.is_subsidiary) {
      node.type = 'subsidiary';
    }

    // TD-1825: Map company_uuid (profile.id) to company_id for navigation
    // company_uuid is the actual profile UUID needed by backend for company details
    // Applies to subsidiaries and company shareholders
    if (node.company_uuid) {
      node.company_id = node.company_uuid;
    }

    node.id = '' + node.member_id;

    if (node.birth_year) {
      node.age = currentYear - node.birth_year + ' years';
    }
  });

  return isIndependent;
};

const mapShareholderLinks = (links, nodes) => {
  links.forEach((edge) => {
    [edge.source_id, edge.target_id] = [edge.target_id, edge.source_id];
    edge.id = edge.source_id + '' + edge.target_id;
    edge.source = edge.source_id;
    edge.target = edge.target_id;
    edge.value = edge.share || PUNCTUATION.EMPTY_VALUE;
    if (nodes.find((n) => n.id === edge.target)?.is_subsidiary) {
      edge.is_subsidiary = true;
    }
  });
};

const logShareholderExclusionsInDevelopment = (excludedLinks, excludedNodes) => {
  const styles = {
    header: 'color: #f87171; font-weight: bold; font-size: 14px;',
    section: 'color: #fb7185; font-weight: bold;',
    label: 'color: #fbbf24; font-weight: bold;',
    muted: 'color: #9ca3af;'
  };

  if (!excludedLinks.length && !excludedNodes.length) {
    return;
  }

  console.log('%c🚨 DEV/STG Logs - Shareholders exclusion report [ START ]', styles.header);

  console.log('%c📋 Data Transformation Info:', styles.section);
  console.log(
    '%cNote:',
    styles.label,
    'Links and nodes have swapped source and target IDs from the original data'
  );

  if (excludedLinks.length) {
    console.log('%c🔗 Excluded Links:', styles.section);
    console.log('%cexcludedLinks:', styles.label, excludedLinks);
    console.log('%cCount:', styles.muted, excludedLinks.length);
  }

  if (excludedNodes.length) {
    console.log('%c🔴 Excluded Nodes:', styles.section);
    console.log('%cexcludedNodes:', styles.label, excludedNodes);
    console.log('%cCount:', styles.muted, excludedNodes.length);
  }

  console.log('%c✅ DEV/STG Logs - Shareholders exclusion report [  END  ]', styles.header);
  console.log('');
};

const logShareholderExclusions = (excludedLinks, excludedNodes) => {
  if (isInDevelopment) {
    logShareholderExclusionsInDevelopment(excludedLinks, excludedNodes);
    return;
  }

  if (excludedLinks.length || excludedNodes.length) {
    console.log('Note: Links and nodes have swapped source and target IDs from the original data');
  }
  if (excludedLinks.length) {
    console.error('Excluded links:', excludedLinks);
  }
  if (excludedNodes.length) {
    console.error('Excluded nodes:', excludedNodes);
  }
};

/**
 * Maps the BE `shareholders` / `relationships` rows to the FE graph structure { nodes, links }.
 *
 * Mutates the rows in place (node types, string ids, company_id, age; link source/target swap so
 * that link.source = owned and link.target = owner, the concatenated link id, value and the
 * is_subsidiary mark), drops links whose endpoints are unknown and then nodes without any link.
 * `nodes` starts as the `shareholders` array itself and is reassigned to the pruned subset.
 *
 * @param {Object[]} [shareholders] - BE shareholder nodes
 * @param {Object[]} [relationships] - BE relationship rows (source_id = owner, target_id = owned)
 * @returns {{ shareholdersStructure: { nodes: Object[], links: Object[] }, isIndependent: boolean }}
 */
export const mapShareholdersStructure = (shareholders, relationships) => {
  const shareholdersStructure = {
    nodes: shareholders || [],
    links: relationships || []
  };

  const isIndependent = mapShareholderNodes(shareholdersStructure.nodes);
  mapShareholderLinks(shareholdersStructure.links, shareholdersStructure.nodes);

  const validNodeIds = new Set(shareholdersStructure.nodes.map((node) => node.id));

  const excludedLinks = [];
  shareholdersStructure.links = shareholdersStructure.links.filter((link) => {
    const isValid = validNodeIds.has(link.source) && validNodeIds.has(link.target);
    if (!isValid) excludedLinks.push(link);
    return isValid;
  });

  const connectedNodeIds = new Set();
  shareholdersStructure.links.forEach((link) => {
    connectedNodeIds.add(link.source);
    connectedNodeIds.add(link.target);
  });

  const excludedNodes = [];
  shareholdersStructure.nodes = shareholdersStructure.nodes.filter((node) => {
    const isConnected = connectedNodeIds.has(node.id);
    if (!isConnected) excludedNodes.push(node);
    return isConnected;
  });

  logShareholderExclusions(excludedLinks, excludedNodes);

  return { shareholdersStructure, isIndependent };
};

/**
 * Ownership share for key contacts who are also shareholders. Mirrors the join the contacts XLSX
 * already performs server-side (lib/contactsDataExport.js). The BE key contacts query sends the
 * share of direct ultimate owners and look-through owners itself, so this join only fills what
 * an older BE leaves out.
 *
 * Pass the raw `shareholders` rows, NOT `shareholdersStructure.nodes`: the graph's nodes are a
 * link-connectivity-pruned subset, which would silently drop owners whose links did not survive.
 *
 * `beneficial_ownership` is only populated by the BE when the row is flagged ultimate, so joining
 * it through surfaces a share on ultimate owners and leaves everyone else with the plain Yes.
 * `member_id` is unique per company (the BE dedupes on it, first row wins), so a Map lookup gives
 * the same result as the export's per-contact find.
 *
 * Pure apart from writing `beneficial_ownership` onto matching contacts that carry none yet;
 * contacts without a match are left untouched. Idempotent and order-independent: it can run when
 * the details payload maps and again when the shareholder graph arrives later, with the same
 * result.
 *
 * @param {Object[]} contacts - key contacts (mutated in place)
 * @param {Object[]} rawShareholders - BE shareholder rows
 * @returns {Object[]} the same contacts array
 */
export const applyShareholderOwnershipToContacts = (contacts, rawShareholders) => {
  if (!Array.isArray(contacts) || !contacts.length) {
    return contacts;
  }

  const shareholdersByMemberId = new Map(
    (rawShareholders || [])
      .filter((shareholder) => shareholder?.member_id != null)
      .map((shareholder) => [String(shareholder.member_id), shareholder])
  );

  if (!shareholdersByMemberId.size) {
    return contacts;
  }

  contacts.forEach((contact) => {
    // Gated on `is_shareholder` exactly as the export is. TD-2124: a look-through
    // (HoldCo) shareholder arrives with its share already set by the BE, and the
    // shareholders JSON nulls the share for owners it does not flag ultimate — so only
    // fill a share the contact does not already carry, as the export does.
    if (
      contact?.is_shareholder &&
      contact.beneficial_ownership == null &&
      contact.person_crefo_id != null
    ) {
      const shareholder = shareholdersByMemberId.get(String(contact.person_crefo_id));
      if (shareholder) {
        contact.beneficial_ownership = shareholder.beneficial_ownership;
      }
    }
  });

  return contacts;
};

/** Body `view` value that selects the graph view on GET_PROFILER_SHAREHOLDER_GRAPH. */
export const SHAREHOLDER_GRAPH_VIEW = 'graph';

/**
 * Maps the graph view payload of POST /get-profiler-shareholders-contacts.
 *
 * Sets and returns `shareholdersStructure` (same transformation as GET_PROFILER_DETAILS),
 * `figuresByMemberId` (NodeFigures by String(member_id)) and `isLegacyPayload`: true when nodes
 * exist but none carries the `company_uuid` key, i.e. an older BE answered with the legacy flow.
 * `data.shareholders` keeps the raw rows for the key-contact ownership join.
 *
 * @param {Object} data - response data { shareholders, relationships } (mutated in place)
 * @returns {{ shareholdersStructure: Object, figuresByMemberId: Map, isLegacyPayload: boolean }}
 */
export const mapShareholderGraphPayload = (data) => {
  if (!data || typeof data !== 'object') {
    return {
      shareholdersStructure: { nodes: [], links: [] },
      figuresByMemberId: new Map(),
      isLegacyPayload: false
    };
  }

  const rawNodes = Array.isArray(data.shareholders) ? data.shareholders : [];
  data.isLegacyPayload =
    rawNodes.length > 0 && !rawNodes.some((node) => node && Object.hasOwn(node, 'company_uuid'));
  data.figuresByMemberId = mapShareholderGraphFigures(rawNodes);

  const { shareholdersStructure, isIndependent } = mapShareholdersStructure(
    data.shareholders,
    data.relationships
  );
  data.shareholdersStructure = shareholdersStructure;
  data.isIndependent = isIndependent;

  return {
    shareholdersStructure: data.shareholdersStructure,
    figuresByMemberId: data.figuresByMemberId,
    isLegacyPayload: data.isLegacyPayload
  };
};

export const API_ENDPOINTS = {
  GET_WEBSOCKET_TOKEN: {
    path: '/get-websocket-token',
    method: REQ_METHODS.GET,
    withCognitoToken: true
  },
  GET_PUBLIC_OPTIONS: {
    path: '/public/nomenclature',
    method: REQ_METHODS.GET
  },
  GET_ACCOUNT_VERIFICATION_PROGRESS: {
    path: '/account-verification-progress',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_ACCOUNT_TYPE: {
    path: '/account-type',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_USER_INFO: {
    path: '/get-user-info',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  EDIT_USER_INFO: {
    path: '/update-user-info',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  DEACTIVATE_USER_ACCOUNT: {
    path: '/deactivate-user',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // TD-2220: fired fire-and-forget from AuthStore.logout via direct fetch
  // (NOT makeRequest) so it dispatches synchronously with the in-memory id
  // token before signOut() clears Cognito local storage. Idempotent on BE;
  // 30-min lazy sweep in td-backend activeSessions.js is the safety net.
  POST_SESSION_END: {
    path: '/session-end',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_PROFILE_COMPANY: {
    path: '/get-profile-company',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  EDIT_PROFILE_COMPANY: {
    path: '/update-profile-company',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  GET_SEARCH_RESULTS: {
    path: '/get-search-results',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_PROJECTS: {
    path: '/list-projects',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_PROJECT_RESULTS: {
    path: '/get-project-results',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  SAVE_PROJECT: {
    path: '/create-project',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_PROJECT: {
    path: '/update-mm-project',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  EXCLUDE_PROJECT_RESULT: {
    path: '/exclude-project-result',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  REQUEST_PROJECT_FEEDBACK: {
    path: '/request-project-feedback',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  RESOLVE_PROJECT_FEEDBACK: {
    path: '/resolve-project-feedback',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_PROJECT_LAST_CHECKED_DATE: {
    path: '/update-project-owner-last-checked-date',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  ARCHIVE_PROJECT: {
    path: '/archive-project',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  DELETE_PROJECTS: {
    path: '/delete-projects',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  GET_SELF_COMPANY_FUNDS: {
    path: '/get-self-company-funds',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_SELF_COMPANY_PORTFOLIOS: {
    path: '/get-self-company-portfolios',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_SELF_COMPANY_INVEST_PROFILES: {
    path: '/get-self-company-invest-profiles',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_SELF_COMPANY_USERS: {
    path: '/get-self-company-users',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_FUNDS: {
    path: '/get-funds',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  EDIT_FUND: {
    path: '/update-funds',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  CREATE_FUND: {
    path: '/create-funds',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  DELETE_FUND: {
    path: '/delete-funds',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  GET_PORTFOLIO_COMPANIES: {
    path: '/get-portfolio-companies',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  EDIT_PORTFOLIO_COMPANY: {
    path: '/update-portfolio-companies',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  CREATE_PORTFOLIO_COMPANY: {
    path: '/create-portfolio-companies',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  DELETE_PORTFOLIO_COMPANY: {
    path: '/delete-portfolio-companies',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  GET_INVEST_PROFILES: {
    path: '/get-invest-profiles',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  EDIT_INVEST_PROFILE: {
    path: '/update-invest-profiles',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  CREATE_INVEST_PROFILE: {
    path: '/create-invest-profiles',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  DELETE_INVEST_PROFILE: {
    path: '/delete-invest-profiles',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  DOCUMENT_PRESIGN: {
    path: '/documents-presign-url',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  POST_OUTREACH_MESSAGE: {
    path: '/send-outreach',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_OUTREACHES: {
    path: '/get-outreaches',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_OUTREACH_MESSAGES: {
    path: '/get-outreaches-messages',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GEO_LOCATION_CITY_AUTOCOMPLETE: {
    path: '/geo-location-city-autocomplete',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  SEND_OUTREACH_MESSAGE: {
    path: '/send-outreach-message',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_OUTREACH_LAST_CHECKED_MESSAGE: {
    path: '/update-outreach-user-last-checked-message',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  ARCHIVE_OUTREACH: {
    path: '/archive-outreach',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  FORWARD_OUTREACH: {
    path: '/forward-outreach',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  REQUEST_OUTREACH_FEEDBACK: {
    path: '/request-outreach-feedback',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  EDIT_OUTREACH_MESSAGE: {
    path: '/edit-outreach-message',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  DELETE_OUTREACH_MESSAGE: {
    path: '/delete-outreach-message',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_DOCUMENT_DOWNLOAD_URL: {
    path: '/document-download',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  RATE_OUTREACH: {
    path: '/rate-outreach',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  MARK_OUTREACH_UNREAD: {
    path: '/mark-outreach-unread',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  ADMIN_LIST_USERS: {
    path: '/admin-list-users',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  ADMIN_UPDATE_USER: {
    path: '/admin-update-user',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  ADMIN_LIST_FPI: {
    path: '/admin-list-fpi',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_PROFILER_SEARCH_RESULTS: {
    path: '/get-profiler-results',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data.results || []).forEach(mapCompanyEntry);
    }
  },
  TRIGGER_PROFILER_SEARCH: {
    path: '/trigger-profiler-search',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  POLL_PROFILER_RESULTS: {
    path: '/poll-profiler-results',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data.results || []).forEach(mapCompanyEntry);
    }
  },
  GET_PROFILER_COMPANIES_BY_INDUSTRIES: {
    path: '/get-profiler-companies-by-industries',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data.results || []).forEach(mapCompanyEntry);
    }
  },
  GET_PROFILER_PROJECTS: {
    path: '/list-profiler-projects',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_PROFILER_PROJECT_RESULTS: {
    path: '/get-profiler-project-results',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data.results || []).forEach(mapCompanyEntry);
    }
  },
  SAVE_PROFILER_PROJECT: {
    path: '/create-profiler-project',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_PROFILER_PROJECT: {
    path: '/update-profiler-project',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  EXCLUDE_PROFILER_PROJECT_RESULT: {
    path: '/exclude-profiler-project-result',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  REQUEST_PROFILER_PROJECT_FEEDBACK: {
    path: '/request-profiler-project-feedback',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  RESOLVE_PROFILER_PROJECT_FEEDBACK: {
    path: '/resolve-profiler-project-feedback',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_PROFILER_PROJECT_LAST_CHECKED_DATE: {
    path: '/update-profiler-project-owner-last-checked-date',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  ARCHIVE_PROFILER_PROJECT: {
    path: '/archive-profiler-project',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  DELETE_PROFILER_PROJECTS: {
    path: '/delete-profiler-projects',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  GET_PROFILER_WATCHLIST: {
    path: '/get-profiler-watch-list',
    withCognitoToken: true,
    method: REQ_METHODS.GET,
    dataMapper: (data) => {
      (data || []).forEach(mapCompanyEntry);
    }
  },
  ADD_TO_PROFILER_WATCHLIST: {
    path: '/add-to-profiler-watch-list',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  UPDATE_PROFILER_WATCHLIST_NEWS_CHECKED: {
    path: '/set-profiler-news-checked',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_COMPANY_LOGO: {
    path: '/get-company-logo',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  GET_SIMILAR_COMPANIES: {
    path: '/get-profiler-comparable-companies',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data?.results || []).forEach(mapCompanyEntry);
      if (data?.referenceCompany) {
        data.comparableData = data.referenceCompany;
        mapCompanyEntry(data.comparableData);
      }
    }
  },
  TRIGGER_PROFILER_COMPARABLE_COMPANIES: {
    path: '/trigger-profiler-comparable-companies',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  POLL_PROFILER_COMPARABLE_COMPANIES: {
    path: '/poll-profiler-comparable-companies',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data?.results || []).forEach(mapCompanyEntry);
      if (data?.referenceCompany) {
        data.comparableData = data.referenceCompany;
        mapCompanyEntry(data.comparableData);
      }
    }
  },
  GET_PROFILER_DETAILS: {
    path: '/get-profiler-details',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    // mockResponseData: () => {
    //   return {
    //     financial_statements: [],
    //     financial_overview: [],
    //     shareholders: [],
    //     relationships: [],
    //     key_contacts: [],
    //     top_management: [],
    //   };
    // },
    dataMapper: (data) => {
      let isFinancialsDataEmpty = false;
      let individual_financial_statements = [];
      let consolidated_financial_statements = [];
      if (Array.isArray(data?.financial_statements)) {
        consolidated_financial_statements = data?.financial_statements.slice();
      } else {
        individual_financial_statements = data?.financial_statements?.individual || [];
        consolidated_financial_statements = data?.financial_statements?.consolidated || [];
      }
      const isFinancialsConsolidatedDataEmpty = !consolidated_financial_statements.length;
      const isFinancialsIndividualDataEmpty = !individual_financial_statements.length;
      isFinancialsDataEmpty = isFinancialsConsolidatedDataEmpty && isFinancialsIndividualDataEmpty;

      data.general = {};
      const financial_overview = data.financial_overview.slice();

      const formatFinancialYear = (ntry) => {
        if (ntry.year && typeof ntry.year !== 'string') {
          try {
            ntry.year = ntry.year.toString();
            ntry.year = new Date(ntry.year).toISOString().split('T')[0];
          } catch (error) {
            console.error('/get-profiler-details dataMapper error > endpoints.js >> ', error);
          }
        }

        try {
          ntry._year = +ntry.year.split('-')[0];
        } catch (error) {
          console.error('/get-profiler-details dataMapper error > endpoints.js >> ', error);
        }
      };

      financial_overview.forEach(formatFinancialYear);
      consolidated_financial_statements.forEach(formatFinancialYear);
      individual_financial_statements.forEach(formatFinancialYear);

      const financialStatementsExtractExtraItemKeys = {
        [PROFILER_FINANCIAL_STATEMENTS.EBIT.name]: PROFILER_FINANCIAL_STATEMENTS.EBIT.xbrl_code,
        [PROFILER_FINANCIAL_STATEMENTS.EBT.name]: PROFILER_FINANCIAL_STATEMENTS.EBT.xbrl_code
      };
      const financialStatementsExtractExtraItemKeysFlat = Object.values(
        financialStatementsExtractExtraItemKeys
      );
      const extracted = {};
      consolidated_financial_statements.forEach((ntry) => {
        const overview_type = financialStatementsExtractExtraItemKeysFlat.find(
          (keyCode) => keyCode === ntry.xbrl_code
        );

        if (overview_type) {
          financial_overview.push({ ...ntry, overview_type });
          extracted[overview_type] = true;
        }
      });

      individual_financial_statements.forEach((ntry) => {
        const overview_type = financialStatementsExtractExtraItemKeysFlat.find(
          (keyCode) => keyCode === ntry.xbrl_code
        );

        if (extracted[overview_type]) {
          return;
        }

        if (overview_type) {
          financial_overview.push({ ...ntry, overview_type });
        }
      });

      const years = [...new Set(financial_overview.map((entry) => entry.year))]
        .map((dateStr) => new Date(dateStr).getTime())
        .sort((a, b) => a - b)
        .map((timestamp) => new Date(timestamp).toISOString().split('T')[0]);

      const generalData = {
        revenues: [],
        grossProfit: [],
        ebitda: [],
        [PROFILER_FINANCIAL_STATEMENTS.EBIT.name]: [],
        [PROFILER_FINANCIAL_STATEMENTS.EBT.name]: [],
        netIncome: [],
        totalAssets: [],
        cashAndEquivalents: [],
        // TD-2277: headcount series for the overview Financials card. Reported
        // (nt.employees) + self-disclosed (sd.employees), one point per year after
        // the BE precedence. Rendered as a count (not currency) at the bottom row.
        employees: []
      };

      // const _formatValueToEM = (ntry) => {
      //   if (ntry.value && !isNaN(ntry.value)) {
      //     if (ntry.value === 0 || ntry.value === '0') {
      //       ntry.value = '0';
      //       return;
      //     }

      //     ntry.value = formatNumber(
      //       (Number(ntry.value) / 1000000).toFixed(1),
      //       PUNCTUATION.COMMA,
      //       true
      //     );
      //   }
      // };

      const _formatPercent = (ntry) => {
        if (ntry.value && ntry.percentage !== null && !isNaN(ntry.percentage)) {
          ntry.percentage = formatPercent(
            (Number(ntry.percentage) * 100).toFixed(1),
            PUNCTUATION.COMMA,
            true
          );
        }
      };

      // TD-2277: key on the BE metric_group instead of raw xbrl_code. Revenue
      // (reported TC/COGS + self-disclosed) and employees (reported + self-
      // disclosed) each collapse to one metric_group; every other row carries
      // its own xbrl_code as its metric_group, so those keys are unchanged.
      financial_overview.forEach((ntry) => {
        // Backward/forward-compatible across the BE deploy window (TD-2401): until
        // metric_group lands on an environment, every row carries its own xbrl_code
        // as its group. The raw revenue/employee codes below keep the overview card
        // populated in that window; once BE is deployed everywhere they're redundant.
        const metricGroup = ntry.metric_group ?? ntry.xbrl_code;
        const parent = {
          [METRIC_GROUP.REVENUE]: {
            group: generalData.revenues,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingTC.grossTradingProfit.totalOutput.netSales': {
            group: generalData.revenues,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingCOGS.grossOpProfit.netSales': {
            group: generalData.revenues,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'sd.revenue': {
            group: generalData.revenues,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingTC.grossTradingProfit': {
            group: generalData.grossProfit,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingCOGS.grossOpProfit': {
            group: generalData.grossProfit,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.EBITDATC': {
            group: generalData.ebitda,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.EBITDACOGS': {
            group: generalData.ebitda,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingTC': {
            group: generalData[PROFILER_FINANCIAL_STATEMENTS.EBIT.name],
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.regular.operatingCOGS': {
            group: generalData[PROFILER_FINANCIAL_STATEMENTS.EBIT.name],
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.EBTTC': {
            group: generalData[PROFILER_FINANCIAL_STATEMENTS.EBT.name],
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome.EBTCOGS': {
            group: generalData[PROFILER_FINANCIAL_STATEMENTS.EBT.name],
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'is.netIncome': {
            group: generalData.netIncome,
            formatters: [/*_formatValueToEM*/ _formatPercent]
          },
          'bs.ass': {
            group: generalData.totalAssets,
            formatters: [
              /*_formatValueToEM*/
            ]
          },
          'bs.ass.currAss.cashEquiv': {
            group: generalData.cashAndEquivalents,
            formatters: [
              /*_formatValueToEM*/
            ]
          },
          // TD-2277: reported + self-disclosed headcount → one employees series.
          // _formatPercent is a no-op until the BE carries an employees growth %
          // (TD-2616); once it does, the overview card's Employees "Growth in %"
          // sub-label renders formatted like Sales.
          [METRIC_GROUP.EMPLOYEES]: {
            group: generalData.employees,
            formatters: [_formatPercent]
          },
          'nt.employees': {
            group: generalData.employees,
            formatters: [_formatPercent]
          },
          'sd.employees': {
            group: generalData.employees,
            formatters: [_formatPercent]
          }
        }[metricGroup];

        if (!parent) {
          return;
        }

        parent.group.push(ntry);
        parent.formatters?.forEach?.((formatter) => {
          formatter(ntry);
        });
      });

      // TD-2277: the overview payload also carries employees rows (nt.employees /
      // sd.employees). Self-disclosed employees can extend to years that have no
      // revenue or any other metric the Financials card renders (the card has no
      // employees row), so those years would show up as fully empty columns.
      // Derive the card's year columns from the metrics actually rendered here
      // (the union of years across generalData) so employees-only — and any other
      // unmapped-code-only — years drop out.
      const overviewYears = [
        ...new Set(
          Object.values(generalData)
            .flat()
            .map((entry) => entry.year)
        )
      ]
        .map((dateStr) => new Date(dateStr).getTime())
        .sort((a, b) => a - b)
        .map((timestamp) => new Date(timestamp).toISOString().split('T')[0]);

      data.general.years = overviewYears;
      Object.entries(generalData).forEach(([key, value]) => {
        data.general[key] = value;
      });

      const grouped_financial_statements = [
        consolidated_financial_statements,
        individual_financial_statements
      ].map((financial_statements) => {
        const groups = [
          {
            id: 'profitAndLoss',
            title: 'Profit & loss statement',
            statement_types: ['profit_and_loss', 'employees'],
            items: [],
            levels: new Set()
          },
          {
            id: 'balanceSheet',
            title: 'Balance sheet',
            statement_types: ['liability', 'asset'],
            items: [],
            levels: new Set()
          }
        ];

        // const nonFormattableValuesKeys = [
        //   PROFILER_FINANCIAL_STATEMENTS.numberOfEmployees.xbrl_code
        // ];

        const detailsYears = [...new Set(financial_statements.map((entry) => entry.year))]
          .map((year) => new Date(year).getTime()) // Convert to timestamp
          .sort((a, b) => a - b) // Sort timestamps
          .map((timestamp) => new Date(timestamp).toISOString().split('T')[0]);

        financial_statements.forEach((ntry) => {
          // Fall back to xbrl_code until metric_group lands on the environment
          // (BE TD-2401). Never group on undefined — that would collapse every
          // untagged row into one item (undefined === undefined).
          const metricGroup = ntry.metric_group ?? ntry.xbrl_code;
          const isEmployeeCount =
            metricGroup === METRIC_GROUP.EMPLOYEES ||
            PROFILER_FINANCIAL_STATEMENTS.numberOfEmployees.xbrl_codes.includes(ntry.xbrl_code);
          if (isEmployeeCount) {
            ntry.value = !isNaN(ntry.value) ? formatNumber(ntry.value) : ntry.value;
          }

          for (let i = 0; i < groups.length; i++) {
            if (groups[i].statement_types.includes(ntry.statement_type)) {
              // TD-2277: group by BE metric_group so reported + self-disclosed
              // revenue (and employees) collapse into one row. For every other
              // row metric_group === xbrl_code, so grouping is unchanged there.
              const foundItem = groups[i].items.find((item) => item.metric_group === metricGroup);
              if (foundItem) {
                foundItem.entries.push(ntry);
                // Merged rows inherit metadata from the first arrival; keep the
                // strongest (lowest) priority so a self-disclosed row arriving
                // first can't push the metric down the P&L ordering.
                foundItem.priority = Math.min(foundItem.priority, ntry.priority || 10000);
              } else {
                const newItem = {
                  name: ntry.description,
                  metric_group: metricGroup,
                  entries: [ntry],
                  priority: ntry.priority || 10000,
                  level: ntry.level || 1
                };
                groups[i].items.push(newItem);
                groups[i].levels.add(newItem.level);
              }

              // if (!nonFormattableValuesKeys.includes(ntry.xbrl_code)) {
              //   _formatValueToEM(ntry);
              // }

              break;
            }
          }
        });

        groups.forEach((group) => {
          group.items.sort((a, b) => a.priority - b.priority);
          group.levels = [...group.levels].sort((a, b) => a - b);
          group.reliability = {};
          group.legal_framework = {};
          const allEntries = group.items.reduce((acc, item) => {
            return [...acc, ...item.entries];
          }, []);
          detailsYears.forEach((year) => {
            const [foundReliability, foundLegalFramework] = [
              allEntries.find((ntry) => ntry.year === year && ntry.reliability)?.reliability,
              allEntries.find((ntry) => ntry.year === year && ntry.legal_framework)?.legal_framework
            ];
            if (foundReliability) {
              group.reliability[year] = foundReliability;
            }
            if (foundLegalFramework) {
              group.legal_framework[year] = foundLegalFramework;
            }
          });
        });

        return { groups, years, detailsYears };
      });

      data.details = {
        consolidated: grouped_financial_statements[0],
        individual: grouped_financial_statements[1]
      };

      data.keyContacts = data.key_contacts;

      // Stays undefined while the backend does not send the field, which hides the Banks tab
      data.bankConnections = data.bank_connections;

      const { shareholdersStructure, isIndependent } = mapShareholdersStructure(
        data.shareholders,
        data.relationships
      );
      data.shareholdersStructure = shareholdersStructure;
      data.isIndependent = isIndependent;

      data.isFinancialsDataEmpty = isFinancialsDataEmpty;
      data.isFinancialsConsolidatedDataEmpty = isFinancialsConsolidatedDataEmpty;
      data.isFinancialsIndividualDataEmpty = isFinancialsIndividualDataEmpty;
      // The backend sends the scope its overview uses; opening the Financials tab on the same
      // scope keeps the two in line. Responses without it fall back to the same rule here.
      data.initialIndividualView = ['consolidated', 'individual'].includes(
        data.financial_overview_scope
      )
        ? data.financial_overview_scope === 'individual'
        : shouldOpenIndividualFinancials(
            consolidated_financial_statements,
            individual_financial_statements
          );

      data.companyDetails = {
        name: data.name,
        country: data.country,
        postcode: data.postcode,
        city: data.city,
        street: data.street,
        email: data.email,
        phone: data.phone
      };

      data.keyContacts = data.key_contacts || [];
      const currentContactsYear = new Date().getFullYear();

      // Ownership share for contacts who are also shareholders (see
      // applyShareholderOwnershipToContacts). Reads the raw `data.shareholders`, not the pruned
      // graph nodes. An empty list (the BE may leave shareholders to the graph view) leaves the
      // contacts untouched; the graph view applies the same join when it arrives.
      applyShareholderOwnershipToContacts(data.keyContacts, data.shareholders);

      // TD-2422: get-profiler-details now sends canonical bucketed role labels
      // (TD-2330) as `contactsRoleFilterOptions` (an ordered string[]) and stamps
      // each key contact with a `role_bucket` label. Prefer the BE's ordered
      // labels; fall back to deriving distinct `role_bucket` values ourselves for
      // resilience. (Previously this rebuilt options from raw `manager_function`,
      // which never lined up with the discovered contacts' Surfe seniority.)
      const beRoleBucketLabels = Array.isArray(data.contactsRoleFilterOptions)
        ? data.contactsRoleFilterOptions.filter((value) => typeof value === 'string')
        : null;
      const contactsRoleFilterOptions = new Set();
      const contactsCountryFilterOptions = new Set();
      let contactsWithoutPersonId = 0;

      data.keyContacts.forEach((contact) => {
        // Every stored contact carries its person id here (discovered rows a `disc_` one).
        // A missing field means the company_managers view changed shape again, which
        // silently disables "View individual profile" (and the ownership join above).
        if (contact.person_crefo_id == null) {
          contactsWithoutPersonId += 1;
        }

        // TD-2079: BE persists enrichment status as 'success'; the FE uses 'found'.
        // Normalise cached reads so previously-revealed contacts display + the
        // bulk skip-cost recognises them.
        normalizeContactEnrichmentStatus(contact);

        if (contact.birth_year) {
          contact.age = currentContactsYear - contact.birth_year;
        }

        if (contact.role_bucket && !contactsRoleFilterOptions.has(contact.role_bucket)) {
          contactsRoleFilterOptions.add(contact.role_bucket);
        }

        if (contact.country && !contactsCountryFilterOptions.has(contact.country)) {
          contactsCountryFilterOptions.add(contact.country);
        }
      });

      if (contactsWithoutPersonId > 0) {
        console.warn(
          `[get-profiler-details] ${contactsWithoutPersonId} of ${data.keyContacts.length} key contacts have no person_crefo_id; their individual profile link is disabled`
        );
      }

      data.contactsRoleFilterOptions = (beRoleBucketLabels ?? [...contactsRoleFilterOptions]).map(
        (value) => ({
          name: value,
          value
        })
      );

      data.contactsCountryFilterOptions = [...contactsCountryFilterOptions].map((value) => ({
        name: value,
        value
      }));

      data.topManagement = (data.key_contacts || []).filter((c) => c.is_top_manager);
    }
  },
  // Shareholder graph view: body { companyId, view: 'graph', sharedProjectId? }. Returns the
  // graph nodes (with card figures) and relationships, no contacts.
  // Requested at page load next to GET_PROFILER_DETAILS; never cancel it via cancelRequest.
  GET_PROFILER_SHAREHOLDER_GRAPH: {
    path: '/get-profiler-shareholders-contacts',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      mapShareholderGraphPayload(data);
    }
  },
  SAVE_MATCHMAKER_CONTACT: {
    path: '/create-custom-contact',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  DELETE_MATCHMAKER_CONTACT: {
    path: '/delete-custom-contact',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  UPDATE_FUNDS_PAGE_VISIT: {
    path: '/update-funds-page-visit',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  GET_PROFILER_COMPANIES_BY_NAME: {
    path: '/get-profiler-companies-by-name',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data || []).forEach(mapCompanySearchNameEntry);
    }
  },
  // Bulk company upload search (TD-2343/TD-2344): upload -> poll -> select -> finalize.
  BULK_COMPANY_SEARCH: {
    path: '/bulk-company-search',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  POLL_BULK_COMPANY_SEARCH: {
    path: '/poll-bulk-company-search',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    // The completed poll payload carries raw fetchCompaniesByName candidates; map them to the
    // CompanyNameResultItem shape here so onSuccess receives ready-to-render rows. Do NOT map
    // again at the call site — mapCompanySearchNameEntry is not idempotent.
    dataMapper: (data) => {
      mapBulkSearchResults(data);
    }
  },
  UPDATE_BULK_COMPANY_SELECTION: {
    path: '/update-bulk-company-selection',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  FINALIZE_BULK_COMPANY_SEARCH: {
    path: '/finalize-bulk-company-search',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // TD-2365: add a user-picked company as a selectable candidate for a not-found row. Response
  // carries the picked company in the raw wire shape; map it once (mapCompanySearchNameEntry is
  // not idempotent) so onSuccess receives a ready-to-render candidate.
  ADD_BULK_COMPANY_CANDIDATE: {
    path: '/add-bulk-company-candidate',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      mapBulkManualCandidate(data);
    }
  },
  GET_PROFILER_COMPANY_FROM_SEARCH: {
    path: '/get-profiler-company-from-search',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      mapCompanyEntry(data.companyData);
    }
  },
  GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA: {
    path: '/get-profiler-company-supplementary-data',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      mapCompanyEntry(data.companyData);
    }
  },
  // Individuals search endpoints
  GET_PROFILER_INDIVIDUALS_BY_NAME: {
    path: '/get-profiler-persons-by-name', // Backend uses "persons" not "individuals"
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      (data || []).forEach(mapIndividualSearchEntry);
    }
  },
  // Individual profile details endpoint
  GET_PROFILER_PERSON_DETAILS: {
    path: '/get-profiler-person-details',
    withCognitoToken: true,
    method: REQ_METHODS.POST,
    dataMapper: (data) => {
      mapIndividualDetailsEntry(data);
    }
  },
  GET_PROFILER_PROJECT_ONE_PAGER_LIST_URL: {
    path: '/export-onepager-list',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  GET_PROFILER_PROJECT_ONE_CONTACT_LIST_URL: {
    path: '/export-contacts-list',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  // TD-2703: bulk "Download company profiles as XLSX" (one row per company).
  // Returns a presigned { url }, same contract as the contacts list.
  GET_PROFILER_PROJECT_PROFILES_LIST_URL: {
    path: '/export-company-profiles-list',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  // Agent Session endpoints
  AGENT_LIST_SESSIONS: {
    path: '/agent-list-sessions',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  AGENT_LIST_SESSION_HISTORY: {
    path: '/agent-list-session-history',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  AGENT_UPDATE_SESSION: {
    path: '/agent-update-session',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  AGENT_DELETE_SESSION: {
    path: '/agent-delete-session',
    withCognitoToken: true,
    method: REQ_METHODS.DELETE
  },
  AGENT_SEARCH_SESSIONS: {
    path: '/agent-search-sessions',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  // Subscription & Usage endpoints (convention: subscription-{verb}-{noun})
  GET_SUBSCRIPTION_STATUS: {
    path: '/subscription-status',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  // TD-2443/TD-2448: record that the current user has seen the fallback / on-hold
  // notification for the current token period. The BE owns the "already notified"
  // state (keyed by user + type + period), so the popup shows once per period
  // across devices/sessions instead of the old per-browser sessionStorage gate.
  // Body: { type: 'fallback' | 'onhold' }.
  SUBSCRIPTION_ACK_NOTIFICATION: {
    path: '/subscription-ack-notification',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // Contact enrichment — single reveal. Path is a prefix; callers append
  // `/{contactEnrichmentId}/reveal` at invocation time. BE returns 200 with
  // `{ status, value, creditsDeducted }`, 202 pending (re-poll), 402 no balance,
  // 404 not found, 410 erasure, 502 surfe failure.
  ENRICHMENT_REVEAL_CONTACT: {
    path: '/enrichment/contact',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // Contact enrichment — bulk reveal. BE returns 202 with
  // `{ jobId, status, totalContacts, accepted, skipped, estimatedCost, surfeEnrichmentId }`,
  // 400 invalid request, 402 insufficient credits, 502 surfe failure.
  ENRICHMENT_BULK_REVEAL: {
    path: '/enrichment/bulk-reveal',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // Contact enrichment — job status polling. Path is a prefix; callers append
  // `/{jobId}/status`. BE returns 200 with
  // `{ jobId, status, totalContacts, enrichedCount, creditsDeducted, completedAt }`.
  // `status` is one of: pending | completed | failed.
  ENRICHMENT_JOB_STATUS: {
    path: '/enrichment/job',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  BULK_DOWNLOAD_PRECHECK: {
    path: '/bulk-download-precheck',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // The shareholder graph PNG is drawn in the browser: this records its download against the
  // allowance before the file is saved (403 DOWNLOAD_LIMIT_EXCEEDED once it is used up).
  EXPORT_PROFILER_SHAREHOLDER_GRAPH: {
    path: '/export-profiler-shareholder-graph',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  SUBSCRIPTION_LIST_COMPANIES: {
    path: '/subscription-list-companies',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  SUBSCRIPTION_GET_COMPANY: {
    path: '/subscription-get-company',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  SUBSCRIPTION_CREATE_COMPANY: {
    path: '/subscription-create-company',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  SUBSCRIPTION_UPDATE_COMPANY: {
    path: '/subscription-update-company',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  SUBSCRIPTION_ADD_DOMAIN: {
    path: '/subscription-add-domain',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // POST (not DELETE) per backend Terraform config — API Gateway uses POST for domain removal
  SUBSCRIPTION_REMOVE_DOMAIN: {
    path: '/subscription-remove-domain',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // Contract periods (TD-2809). A contract is an ordered sequence of periods;
  // the limits the platform enforces live on the company and are mirrored from
  // whichever period is active, so raising a limit means editing a period.
  SUBSCRIPTION_LIST_PERIODS: {
    path: '/subscription-list-periods',
    withCognitoToken: true,
    method: REQ_METHODS.GET
  },
  SUBSCRIPTION_CREATE_PERIOD: {
    path: '/subscription-create-period',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  SUBSCRIPTION_UPDATE_PERIOD: {
    path: '/subscription-update-period',
    withCognitoToken: true,
    method: REQ_METHODS.PUT
  },
  // POST (not DELETE) per backend Terraform config, like domain removal above.
  SUBSCRIPTION_END_PERIOD: {
    path: '/subscription-end-period',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },

  // MFA endpoints (TD-1976). Backend requires both Authorization (ID token)
  // AND x-cognito-access-token on setup/verify/disable; status only needs Authorization.
  MFA_SETUP: {
    path: '/mfa/setup',
    method: REQ_METHODS.POST,
    withCognitoToken: true,
    withCognitoAccessToken: true
  },
  MFA_VERIFY: {
    path: '/mfa/verify',
    method: REQ_METHODS.POST,
    withCognitoToken: true,
    withCognitoAccessToken: true
  },
  MFA_STATUS: {
    path: '/mfa/status',
    method: REQ_METHODS.GET,
    withCognitoToken: true
  },
  MFA_DISABLE: {
    path: '/mfa/disable',
    method: REQ_METHODS.DELETE,
    withCognitoToken: true,
    withCognitoAccessToken: true
  },
  // Concurrent-session enforcement (TD-2239). Distinct from AGENT_LIST_SESSIONS
  // above (agent chat sessions) — this is the concurrent login/usage slot.
  // POST /session-claim {force?}: claim-or-evict. Drives the login check, the
  // focus/route re-check, and the take-over (force:true). Replaces the old
  // /session-list + /session-takeover pair.
  SESSION_CLAIM: {
    path: '/session-claim',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // TD-2105 (Contact Enrichment Stream B): discover contacts by job title +
  // management level via Surfe people-search. Discovery is FREE (no credit
  // cost — only reveal costs). Request body is { profileId, criteria }; the BE
  // scopes the search to the on-screen company server-side from profileId, so
  // the FE never sends company name/domain.
  // Response is a FLAT body { contacts, totalBeforeDedupe, totalAfterDedupe } (no
  // { success, data } envelope). There is deliberately no `dataMapper` here:
  // api.js only invokes an endpoint's dataMapper on the wrapped `{ success }`
  // (mock) path, so a mapper declared on this flat endpoint would silently never
  // run against the real BE. The caller (DiscoverContactsPanel) maps the flat
  // body via `mapDiscoverResponse`, mirroring the reveal endpoints.
  DISCOVER_CONTACTS: {
    path: '/enrichment/discover',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  },
  // TD-2104 (Contact Enrichment Stream B): first reveal of a Surfe-discovered
  // contact. Persists the ephemeral contact (BE mints person_crefo_id
  // disc_<uuid>, source 'discovered') then reveals the requested field,
  // returning the single-reveal shape PLUS the freshly-minted
  // `contact_enrichment_id`. Body:
  // { profileId, contact: { first_name, last_name, linkedin_url? }, field }.
  // Subsequent reveals on the same contact use ENRICHMENT_REVEAL_CONTACT with
  // that returned id.
  ENRICHMENT_DISCOVER_REVEAL: {
    path: '/enrichment/discover-reveal',
    withCognitoToken: true,
    method: REQ_METHODS.POST
  }
};
